import React from 'react';
import styled from 'styled-components';
import { ButtonIcon } from 'components/Button';
import { Empty } from 'components/Empty';
import { User } from 'components/User';
import { theme, toRem } from 'design';
import { IPayee } from 'types';
import { formatJobTitle } from 'utils';
import Sidebar from './components/Sidebar';
import { Content as BaseContent } from './components/styles';

interface IPayeesSidebarProps {
  dataCy?: string;
  payees?: IPayee[];
  isLoading?: boolean;
  onBack: () => void;
  onContinue: () => void;
  onRemovePayee: (index: number) => void;
}

const Content = styled(BaseContent)`
  height: 100%;
`;

const PayeesList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  list-style: none;
  padding: ${toRem(theme.space.lg)} 0;
  gap: ${toRem(theme.space.md)};
`;

const PayeeListItemLayout = styled.li`
  display: flex;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(72)};
  padding: 0 ${toRem(theme.space.xl)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.white};
  word-break: break-word;
  overflow-wrap: anywhere;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PayeeListItem = ({ payee, onRemove, dataCy }: { payee: IPayee; onRemove: () => void; dataCy: string }) => {
  const { employee } = payee;
  return (
    <PayeeListItemLayout data-cy={`${dataCy}-payee-list-item`}>
      <User id={payee.id} username={payee.name} image={employee.user?.avatarUrl} role={formatJobTitle(employee.job.title)} hasPaymentMethod={!!employee.user?.card} stackable={false} />
      <ButtonIcon data-cy="remove-btn" label={`Remove ${payee.name} from payees.`} icon="Close" appearance="secondary" size="small" onClick={onRemove} />
    </PayeeListItemLayout>
  );
};

const PayeesSideBar = ({ payees = [], onBack, onContinue, onRemovePayee, isLoading, dataCy = 'payees-sidebar' }: IPayeesSidebarProps) => (
  <Sidebar
    title={`Payees ${payees?.length > 0 ? `(${payees.length})` : ''}`}
    leftActionButton={{ label: 'Back', appearance: 'white', onClick: onBack }}
    rightActionButton={{ label: 'Continue', appearance: 'primary', disabled: !payees.length || isLoading, onClick: onContinue }}
    dataCy={dataCy}
  >
    <Content>
      {!payees.length ? (
        <Empty container icon="Group" message="Payees will appear here as you add them." />
      ) : (
        <PayeesList data-cy={`${dataCy}-payees-list`}>
          {payees.map((payee, idx) => (
            <PayeeListItem key={payee.id} payee={payee} onRemove={() => onRemovePayee?.(idx)} dataCy={dataCy} />
          ))}
        </PayeesList>
      )}
    </Content>
  </Sidebar>
);

export default PayeesSideBar;
